import { useCallback, useEffect, useState } from 'react';
import { Handshake } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import Ayuda from '../ui/Ayuda';
import Modal from '../ui/Modal';
import { Seccion, CampoValidado } from '../ui/Formulario';
import TiempoRelativo from '../ui/TiempoRelativo';
import { Cargando } from '../ui/EstadoDePantalla';

// =====================================================================
// LOS PLAZOS CON LOS QUE LA EMPRESA NEGOCIA
// =====================================================================
// Hasta la migración 160 estos números vivían quemados en el código, o
// directamente no existían. Quien negocia con un proveedor no puede
// desplegar una versión para cambiar su plazo de pago.
//
// LA PANTALLA LLEGA VACÍA A PROPÓSITO. Son dinero y plazos: los escribe
// el dueño. Un número puesto por nosotros se leería como recomendación
// de la casa sobre el negocio de otro.

// A quién favorece el valor alto. Se ENSEÑA, y ésa es la Regla 0 del
// encargo: una ventaja que depende de que la contraparte no se entere se
// cobra una vez; una que se puede poner sobre la mesa se cobra en cada
// operación del año.
const FAVOR = {
  NOSOTROS:    { texto: 'Más alto te conviene a ti',        clase: 'text-green-700 bg-green-50 border-green-200' },
  CONTRAPARTE: { texto: 'Más alto le conviene a la otra parte', clase: 'text-amber-800 bg-amber-50 border-amber-200' },
  NEUTRO:      { texto: 'No inclina la balanza',            clase: 'text-gray-600 bg-gray-50 border-gray-200' },
};

const UNIDAD = { DIAS: 'días', HORAS: 'horas', PORCENTAJE: '%', ENTERO: '', DECIMAL: '' };

function valorLegible(p) {
  if (p.valor_actual === null || p.valor_actual === undefined) return null;
  const n = Number(p.valor_actual);
  const limpio = Number.isInteger(n) ? String(n) : String(n).replace(/0+$/, '');
  return `${limpio} ${UNIDAD[p.tipo] ?? ''}`.trim();
}

export default function ParametrosComerciales() {
  // EL PERMISO SE LEE AQUI, Y NO LLEGA COMO PROPIEDAD.
  //
  // El defecto, encontrado el 1 de septiembre de 2026 al montar la
  // pantalla de la Fase 5: `AppShell` monta cada pantalla con `irA` y
  // `contexto`, y nada mas. `puedeEditar` nunca llegaba, se quedaba en
  // su valor por omision —`false`— y **la pantalla no dejaba cambiar
  // ningun parametro a nadie**, ni al dueño. Se veian los plazos y no se
  // podian tocar, que es exactamente lo contrario de lo que esta
  // pantalla existe para hacer.
  //
  // No se noto porque una propiedad que no llega no falla: se comporta
  // como si la persona no tuviera el permiso, y eso se parece
  // demasiado a que el permiso no este puesto.
  const { puede } = useAuth();
  const puedeEditar = puede('parametros_comerciales');
  const [lista, setLista] = useState([]);
  const [clausulas, setClausulas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editar, setEditar] = useState(null);
  const [redactar, setRedactar] = useState(null);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [ps, cs] = await Promise.all([
        api.get('/parametros'), api.get('/parametros/clausulas'),
      ]);
      setLista(ps);
      setClausulas(cs);
      setError('');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar la lista.');
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const columnas = [
    { etiqueta: 'Parámetro', valor: (p) => p.nombre, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Valor',
      alineado: 'derecha',
      valor: (p) => valorLegible(p) ?? '',
      render: (p) => {
        const v = valorLegible(p);
        // SIN DECIDIR NO ES CERO, y la pantalla no puede enseñar lo
        // mismo para las dos cosas: cero en la tolerancia significa «no
        // se perdona ni un día» —una postura— y sin decidir significa
        // que nadie ha escrito el número todavía.
        return v
          ? <span className="tabular-nums font-semibold">{v}</span>
          : <span className="text-amber-800 text-xs">Sin decidir</span>;
      },
    },
    {
      etiqueta: 'A quién favorece',
      valor: (p) => FAVOR[p.favorece_alto]?.texto ?? '',
      render: (p) => {
        const f = FAVOR[p.favorece_alto] ?? FAVOR.NEUTRO;
        return <span className={`text-xs px-2 py-0.5 rounded-full border ${f.clase}`}>{f.texto}</span>;
      },
    },
    {
      etiqueta: 'Último cambio',
      valor: (p) => p.vigente_desde ?? '',
      render: (p) => (p.vigente_desde
        ? <span className="text-xs text-gray-500">
            <TiempoRelativo momento={p.vigente_desde} />
            {p.puesto_por && <> · {p.puesto_por}</>}
          </span>
        : <span className="text-xs text-gray-600">—</span>),
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Reglas de compra"
        subtitulo="Los plazos y tolerancias con los que tu empresa negocia. Cambiarlos no toca ninguna operación ya cerrada."
        nota={<Ayuda id="parametros.pantalla" />}
        columnas={columnas}
        filas={lista}
        cargando={cargando}
        acciones={puedeEditar
          ? (p) => [{ etiqueta: p.valor_actual === null ? 'Poner valor' : 'Cambiar',
                      onClick: () => setEditar(p) }]
          : undefined}
        vacio={
          <EstadoVacio
            icono={Handshake}
            titulo="Todavía no hay parámetros que configurar"
            descripcion="Aparecerán aquí en cuanto se instale la actualización que los trae."
          />
        }
      />

      {error && (
        <div role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      <SeccionClausulas
        huecos={clausulas}
        cargando={cargando}
        puedeEditar={puedeEditar}
        onEscribir={setRedactar}
      />

      {editar && (
        <ModalValor
          parametro={editar}
          onCerrar={() => setEditar(null)}
          onListo={() => { setEditar(null); cargar(); }}
        />
      )}

      {redactar && (
        <ModalClausula
          hueco={redactar}
          onCerrar={() => setRedactar(null)}
          onListo={() => { setRedactar(null); cargar(); }}
        />
      )}
    </>
  );
}

// =====================================================================
// LAS CLAUSULAS · Fase 6.2 de DIRECTRIZ-VENTAJA
// =====================================================================
// LA LINEA QUE NO SE CRUZA: TEGO no redacta clausulas. Deja el hueco
// donde va cada una, dice a quien le llegara lo que se escriba, y ahi se
// para. Un texto de fabrica se leeria como redaccion revisada por
// alguien, viajaria en ordenes de compra reales, y el dia que no
// aguantara el problema no seria de quien lo pego.
//
// Y VIVE EN ESTA PANTALLA, con los plazos, porque es la misma decision
// de la misma persona: poner una penalizacion del 1 % diario sin
// escribir la clausula que la sostiene es tener un calculo y no poder
// cobrarlo.

export function TarjetaClausula({ hueco, puedeEditar, onEscribir }) {
  const escrita = !!hueco.texto;
  return (
    <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-gray-900">{hueco.nombre}</p>
          <p className="text-xs text-gray-500 mt-0.5">{hueco.donde}</p>
        </div>
        {escrita
          ? <span className="text-xs px-2 py-0.5 rounded-full border shrink-0
                             text-green-700 bg-green-50 border-green-200">
              Se imprime
            </span>
          : <span className="text-xs px-2 py-0.5 rounded-full border shrink-0
                             text-amber-800 bg-amber-50 border-amber-200">
              Sin escribir
            </span>}
      </div>

      <p className="text-sm text-gray-600 leading-relaxed">{hueco.que_cubre}</p>

      {escrita ? (
        <blockquote className="text-sm text-gray-800 bg-gray-50 border-l-2
                               border-gray-300 pl-3 py-2 whitespace-pre-wrap">
          {hueco.texto}
        </blockquote>
      ) : (
        // ESTADO VACIO UTIL, y dice la consecuencia exacta: mientras no
        // haya texto, la orden sale sin esta clausula. Nada se rompe;
        // lo que no se puede es cobrar lo que ella sostendria.
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200
                      rounded p-3">
          Tus órdenes de compra salen sin esta cláusula. Pega aquí el texto que
          te apruebe tu abogado y se imprimirá al calce desde la siguiente.
        </p>
      )}

      <div className="flex items-center justify-between gap-3 mt-1">
        <span className="text-xs text-gray-500">
          {hueco.vigente_desde ? (
            <>
              <TiempoRelativo momento={hueco.vigente_desde} />
              {hueco.puesto_por && <> · {hueco.puesto_por}</>}
            </>
          ) : '—'}
        </span>
        {puedeEditar && (
          <button type="button" onClick={() => onEscribir(hueco)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            {escrita ? 'Cambiar texto' : 'Escribir cláusula'}
          </button>
        )}
      </div>
    </div>
  );
}

export function SeccionClausulas({ huecos, cargando, puedeEditar, onEscribir }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900">
        Cláusulas de tus órdenes de compra
      </h2>
      <p className="text-sm text-gray-600 mt-1">
        El texto legal que se imprime al calce de la orden que recibe tu
        proveedor. Lo escribe tu abogado: TEGO deja el hueco y no redacta
        ninguna.
      </p>
      <div className="mt-2"><Ayuda id="clausulas.seccion" /></div>

      {cargando && <Cargando filas={4} />}

      <div className="mt-4 grid gap-3">
        {huecos.map((h) => (
          <TarjetaClausula key={h.clave} hueco={h} puedeEditar={puedeEditar}
                           onEscribir={onEscribir} />
        ))}
      </div>
    </section>
  );
}

const MINIMO_CLAUSULA = 10;

export function ModalClausula({ hueco, onCerrar, onListo }) {
  const [texto, setTexto] = useState(hueco.texto ?? '');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  // VALIDACION EN VIVO, criterio 8 de la Doctrina 2: se dice mientras se
  // escribe, no al guardar.
  const problema = texto.trim().length < MINIMO_CLAUSULA
    ? `Escribe al menos ${MINIMO_CLAUSULA} caracteres, o cierra sin guardar: `
      + 'una cláusula a medias impresa en una orden la firma el proveedor igual.'
    : null;

  async function guardar(e) {
    e.preventDefault();
    if (problema) return;
    setGuardando(true);
    try {
      await api.put(`/parametros/clausulas/${hueco.clave}`, { texto });
      onListo();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal titulo={hueco.nombre} onCerrar={onCerrar}>
      <form onSubmit={guardar} className="flex flex-col gap-4">
        <p className="text-sm text-gray-600 leading-relaxed">{hueco.que_cubre}</p>

        <Seccion titulo="El texto, tal cual te lo dé tu abogado" columnas={1}>
          <CampoValidado
            etiqueta="Texto de la cláusula"
            requerido
            valor={texto}
            validar={() => problema}
            ayuda="Se imprime tal cual, sin cambiarle una coma. TEGO no revisa ni corrige lo que escribas aquí."
          >
            {(props) => (
              <textarea {...props} rows={8} maxLength={4000} value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        placeholder="Pega aquí el texto aprobado por tu abogado" />
            )}
          </CampoValidado>
        </Seccion>

        <Ayuda id="clausulas.vigencia" />

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            Cancelar
          </button>
          {/* DESHABILITADO CON EL MOTIVO A LA VISTA. Doctrina 2, criterio 8. */}
          <button type="submit" disabled={!!problema || guardando}
                  title={problema ?? undefined}
                  className="rounded-lg bg-[var(--color-marca)] px-3 py-2 text-sm
                             font-semibold text-white disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar cláusula'}
          </button>
        </div>
        {problema && (
          <p className="text-xs text-amber-800 text-right -mt-2">{problema}</p>
        )}
      </form>
    </Modal>
  );
}

function ModalValor({ parametro, onCerrar, onListo }) {
  const [valor, setValor] = useState(
    parametro.valor_actual !== null && parametro.valor_actual !== undefined
      ? String(Number(parametro.valor_actual)) : '');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const fuera = () => {
    if (valor === '') return 'Escribe un número.';
    const n = Number(valor);
    if (Number.isNaN(n)) return 'Tiene que ser un número.';
    if (parametro.minimo !== null && n < Number(parametro.minimo))
      return `No puede ser menos de ${Number(parametro.minimo)}.`;
    if (parametro.maximo !== null && n > Number(parametro.maximo))
      return `No puede pasar de ${Number(parametro.maximo)}.`;
    return null;
  };
  const problema = fuera();

  async function guardar(e) {
    e.preventDefault();
    if (problema) return;
    setGuardando(true);
    try {
      await api.put(`/parametros/${parametro.clave}`,
                    { valor, motivo: motivo.trim() || null });
      onListo();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal titulo={parametro.nombre} onCerrar={onCerrar}>
      <form onSubmit={guardar} className="flex flex-col gap-4">
        <p className="text-sm text-gray-600 leading-relaxed">{parametro.efecto}</p>

        <Seccion titulo="El valor" columnas={1}>
          <CampoValidado
            etiqueta={`Valor en ${UNIDAD[parametro.tipo] || 'unidades'}`}
            requerido
            valor={valor}
            validar={() => fuera()}
            ayuda={parametro.minimo !== null && parametro.maximo !== null
              ? `Entre ${Number(parametro.minimo)} y ${Number(parametro.maximo)}.`
              : undefined}
          >
            {(props) => (
              <input {...props} type="number" step="any" value={valor}
                     onChange={(e) => setValor(e.target.value)}
                     className={`${props.className} tabular-nums`} />
            )}
          </CampoValidado>

          <CampoValidado
            etiqueta="Por qué se cambia (opcional)"
            valor={motivo}
            ayuda="Dentro de un año, esto es lo que explica el número."
          >
            {(props) => (
              <input {...props} maxLength={240} value={motivo}
                     onChange={(e) => setMotivo(e.target.value)}
                     placeholder="Contrato nuevo con el proveedor" />
            )}
          </CampoValidado>
        </Seccion>

        <Ayuda id="parametros.vigencia" />

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            Cancelar
          </button>
          {/* DESHABILITADO CON EL MOTIVO A LA VISTA. Doctrina 2, criterio 8. */}
          <button type="submit" disabled={!!problema || guardando}
                  title={problema ?? undefined}
                  className="rounded-lg bg-[var(--color-marca)] px-3 py-2 text-sm
                             font-semibold text-white disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
        {problema && (
          <p className="text-xs text-amber-800 text-right -mt-2">{problema}</p>
        )}
      </form>
    </Modal>
  );
}
